
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { SkipLink } from './components/AccessibilityUtils';
import { SimpleLoginPage } from './pages/SimpleLoginPage';
import { TeamSelectionPage } from './pages/TeamSelectionPage';
import { DashboardSkeleton, ScheduleSkeleton, EquipmentSkeleton } from './utils/lazyLoading';

// Lazy load heavy pages using React.lazy directly
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const TeamManagementPage = React.lazy(() => import('./pages/TeamManagementPage'));
const MemberManagementPage = React.lazy(() => import('./pages/MemberManagementPage'));
const ScheduleManagementPage = React.lazy(() => import('./pages/ScheduleManagementPage'));
const ScheduleDetailPage = React.lazy(() => import('./pages/ScheduleDetailPage'));
const AttendancePage = React.lazy(() => import('./pages/AttendancePage'));
const EquipmentManagementPage = React.lazy(() => import('./pages/EquipmentManagementPage'));
const NotificationManagementPage = React.lazy(() => import('./pages/NotificationManagementPage'));
import { PublicRoute, AuthGuard } from './components/AuthGuard';
import './App.css';

function App() {

  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <SkipLink href="#main-content">メインコンテンツにスキップ</SkipLink>
              <main id="main-content">
                <Routes>
                {/* パブリックルート - シンプル認証のみ */}
                <Route path="/login" element={
                  <PublicRoute>
                    <SimpleLoginPage />
                  </PublicRoute>
                } />
                
                {/* チーム選択（新規ユーザー用） */}
                <Route path="/team-selection" element={<TeamSelectionPage />} />
                
                {/* 認証が必要なルート */}
                <Route path="/dashboard" element={
                  <AuthGuard>
                    <React.Suspense fallback={<DashboardSkeleton />}>
                      <DashboardPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/team" element={
                  <AuthGuard>
                    <React.Suspense fallback={<DashboardSkeleton />}>
                      <TeamManagementPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/members" element={
                  <AuthGuard>
                    <React.Suspense fallback={<DashboardSkeleton />}>
                      <MemberManagementPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/schedules" element={
                  <AuthGuard>
                    <React.Suspense fallback={<ScheduleSkeleton />}>
                      <ScheduleManagementPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/schedules/:scheduleId" element={
                  <AuthGuard>
                    <React.Suspense fallback={<ScheduleSkeleton />}>
                      <ScheduleDetailPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/schedules/:scheduleId/attendance" element={
                  <AuthGuard>
                    <React.Suspense fallback={<ScheduleSkeleton />}>
                      <AttendancePage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/equipment" element={
                  <AuthGuard>
                    <React.Suspense fallback={<EquipmentSkeleton />}>
                      <EquipmentManagementPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                <Route path="/notifications" element={
                  <AuthGuard>
                    <React.Suspense fallback={<DashboardSkeleton />}>
                      <NotificationManagementPage />
                    </React.Suspense>
                  </AuthGuard>
                } />
                
                {/* デフォルトリダイレクト */}
                <Route path="/" element={
                  <AuthGuard>
                    <Navigate to="/dashboard" replace />
                  </AuthGuard>
                } />
                
                {/* 404ページ */}
                <Route path="*" element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                    <div className="text-center max-w-md mx-auto">
                      <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-4">404</h1>
                      <p className="text-gray-600 mb-6 text-sm sm:text-base">
                        お探しのページが見つかりません
                      </p>
                      <a 
                        href="/dashboard" 
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      >
                        ダッシュボードに戻る
                      </a>
                    </div>
                  </div>
                } />
                </Routes>
              </main>
            </div>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;